import { useQuery } from "@apollo/client";
import { Button, Typography } from "@mui/material";
import { Field, FieldProps } from "formik";
import { ReactElement } from "react";

import {
  ListCreatomateTemplatesQuery,
  ListCreatomateTemplatesQueryVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import { LIST_CREATOMATE_TEMPLATES } from "~frontend/graphql/personalizedGifs";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";

import { TemplatePreview } from "./TemplatePreview";
import { CreatomateTemplate } from "./types";

export function SelectTemplateField(): ReactElement {
  const { subaccount } = useSubaccount()!;

  const personalizedGifTemplatesQuery = useQuery<
    ListCreatomateTemplatesQuery,
    ListCreatomateTemplatesQueryVariables
  >(LIST_CREATOMATE_TEMPLATES, {
    variables: { input: { subaccountId: subaccount.id } },
  });

  if (!personalizedGifTemplatesQuery.data) {
    return <Loading />;
  }

  const templates = personalizedGifTemplatesQuery.data.listCreatomateTemplates;

  return (
    <Field name="template">
      {({ field, form }: FieldProps<CreatomateTemplate | null>) => {
        const handleSelectTemplate = async (
          template: CreatomateTemplate | null
        ) => {
          // reset all customizations when template changes
          await form.resetForm();

          await form.setFieldValue(field.name, template);
        };

        return (
          <FlexBox flexWrap="wrap" gap="24px 8px">
            {templates.map(template => {
              const selected =
                template.creatomateId === field.value?.creatomateId;
              const onClick = selected
                ? () => handleSelectTemplate(null)
                : () => handleSelectTemplate(template);

              return (
                <FlexBox
                  key={template.creatomateId}
                  sx={{
                    backgroundColor: selected ? palette.lightGray : null,
                    padding: "8px",
                  }}
                  flexDirection="column"
                  gap="8px"
                  alignItems="center"
                >
                  <TemplatePreview
                    template={template}
                    onClick={onClick}
                    pauseOnMouseOff
                  />
                  <FlexBox
                    sx={{ width: "240px" }}
                    alignItems="center"
                    justifyContent="space-between"
                    gap="8px"
                  >
                    <Typography>
                      {template.displayName || "Untitled"}
                    </Typography>
                    {selected ? (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={onClick}
                      >
                        Selected
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="tertiary"
                        onClick={onClick}
                      >
                        Select
                      </Button>
                    )}
                  </FlexBox>
                </FlexBox>
              );
            })}
          </FlexBox>
        );
      }}
    </Field>
  );
}
